import React from 'react'

import { FeaturePromoEmptyState, type FeaturePromoEmptyStateProps } from '../../../../../views/Plan/Common/FeaturePromoEmptyState/FeaturePromoEmptyState'
import { useAutoMeetingPrep } from '../../../../../views/Setup/Settings/AutoMeetingPrep/hooks'
import { AIAgentLoader } from '../../../../../views/AIAgent/AIAgentLoader/AIAgentLoader'

type MeetingPrepEmptyStateVariant = 'advisor' | 'admin' | 'autoPrepOn'

const HELP_LINK = { label: 'Learn how Meeting Prep works', href: 'https://help.zocks.io/en/articles/16004450-prepare-for-meetings-with-the-ai-assistant' }
const VIEW_UPCOMING_MEETINGS = { label: 'View upcoming meetings', to: '/workspace/upcoming' }

const ADVISOR_PROPS: FeaturePromoEmptyStateProps = {
  title: 'Be ready for your next meeting',
  description: "This meeting didn't have a Meeting Prep. Open your next meeting to get one. It includes a summary, draft agenda and open tasks.",
  primaryAction: VIEW_UPCOMING_MEETINGS,
  helpLink: HELP_LINK,
}

const VARIANT_PROPS: Record<MeetingPrepEmptyStateVariant, FeaturePromoEmptyStateProps> = {
  advisor: { ...ADVISOR_PROPS, footnote: 'Ask your admin to turn on automatic Meeting Prep.' },
  admin: {
    title: 'Be ready for every meeting',
    description:
      "This meeting didn't have a Meeting Prep. Turn on automatic prep to get one before each meeting. It includes a summary, draft agenda and open tasks.",
    primaryAction: { label: 'Turn on automatic prep', to: '/workspace/setup/settings/ai-assistant' },
    secondaryAction: VIEW_UPCOMING_MEETINGS,
    footnote: 'You can turn it off anytime in Settings.',
    helpLink: HELP_LINK,
  },
  autoPrepOn: ADVISOR_PROPS,
}

export const MeetingPrepEmptyState: React.FC = () => {
  const { autoMeetingPrepWorkflow, autoMeetingPrepLoading, autoMeetingPrepQuotaEnabled, hasWorkflowUpdateScope } = useAutoMeetingPrep()

  if (autoMeetingPrepLoading) {
    return <AIAgentLoader withChat={false} />
  }

  const canEnableAutoPrep = hasWorkflowUpdateScope && autoMeetingPrepQuotaEnabled
  const variant: MeetingPrepEmptyStateVariant = autoMeetingPrepWorkflow ? 'autoPrepOn' : canEnableAutoPrep ? 'admin' : 'advisor'

  return (
    <div className='zocks-mt-6'>
      <FeaturePromoEmptyState {...VARIANT_PROPS[variant]} />
    </div>
  )
}
